import { useRef, type ReactNode } from 'react';
import { ActionIcon, Badge, Button } from '@repletoryreach/sdk-ui';
import { Menu, Tooltip } from '@repletoryreach/sdk-ui/overlays';
import { useElementSize } from '@repletoryreach/sdk-utils/hooks';
import type { ConversationSla, ConversationSummary } from '@repletoryreach/sdk-api/inbox';
import type { TicketSummary } from '@repletoryreach/sdk-api/tickets';
import { useT } from '../../../i18n/I18nProvider';
import { formatDateTime } from '../../../lib/format';
import { SlaBadge } from './SlaBadge';
import { SessionWindowBadge, type SessionWindowState } from './SessionWindow';
import { ContactAvatar, WhatsAppGlyph } from './ContactAvatar';
import { ChannelMark } from './ChannelMark';
import { LinkedTicketChip } from './LinkedTicket';

/**
 * Below this content width the actions drop their words and keep their icons,
 * so the header stays one row beside a docked sidebar. Measured on the header
 * rather than the window, because the sidebar and the console's navigation
 * both take width the window does not know about.
 */
const COMPACT_BELOW = 720;

export interface ThreadHeaderProps {
  conversation: ConversationSummary;
  sla: ConversationSla | null | undefined;
  now: number;
  sessionWindow: SessionWindowState;
  stateCode: string;
  closed: boolean;
  onTransfer: () => void;
  onResolve: () => void;
  onLock: () => void;
  lockDisabled: boolean;
  lockHidden: boolean;
  resolving: boolean;
  locking: boolean;
  /** Opens the settings sidebar at the contact section. */
  onOpenContact: () => void;
  onBack?: () => void;
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  /** The sidebar's element id, for `aria-controls`. */
  sidebarId: string;
  /** The bot is staying out of this thread. */
  botPaused: boolean;
  /** When it answers again by itself; null when it waits for a person. */
  botResumesAt: string | null;
  /** The team blocked this customer. */
  blocked: boolean;
  /**
   * Take over and hand back (4 October 2026); null on a record of calls. `name` is who has the chat
   * ("you" or a teammate), null when the bot has it; `from` is the bot's name.
   */
  takeover: { byMe: boolean; name: string | null; from: string | null; pending: boolean; onTakeOver: () => void; onHandBack: () => void } | null;
  /** Book an appointment for this conversation's contact (7 October 2026); absent where booking does not apply. */
  onBook?: () => void;
  /** Open a ticket for this conversation (7 October 2026). */
  onCreateTicket?: () => void;
  /** The ticket this conversation is linked to, shown as a chip; null when there is none. */
  linkedTicket?: TicketSummary | null;
}

/**
 * The thread header: who, the clocks, and the actions. Nothing else.
 *
 * It used to carry the assignee, tags and spend as labelled columns as well,
 * which made it two or three rows tall on an ordinary laptop and took that
 * height straight out of the message history. Those facts moved to the
 * settings sidebar (`ConversationSidebar`). The reply window and the SLA stay
 * here, because they are clocks: an agent needs to see them while reading, not
 * after opening a panel.
 */
export function ThreadHeader({
  conversation, sla, now, sessionWindow, stateCode, closed,
  onTransfer, onResolve, onLock, lockDisabled, lockHidden, resolving, locking,
  onOpenContact, onBack, sidebarOpen, onToggleSidebar, sidebarId, botPaused, botResumesAt, blocked, takeover, onBook, onCreateTicket, linkedTicket
}: ThreadHeaderProps) {
  const t = useT();
  const headerRef = useRef<HTMLElement>(null);
  const { width } = useElementSize(headerRef);
  const compact = width > 0 && width < COMPACT_BELOW;
  // No telephone number on Messenger or Instagram, so the fallback is the
  // channel's own id for the person rather than an empty heading.
  const name = conversation.contact_display_name ?? conversation.contact_msisdn ?? conversation.contact_ref;
  const channel = conversation.channel ?? 'whatsapp';
  // An email thread (4 October 2026) is known by its subject as much as by the person.
  const subject = channel === 'email' ? conversation.subject?.trim() || null : null;

  return (
    <header className="rr-inbox2__header" ref={headerRef} data-guide="inbox-header">
      <div className="rr-inbox2__header-who">
        {onBack && (
          <ActionIcon variant="default" size="sm" aria-label={t('action.back')} onClick={onBack}>
            <BackIcon />
          </ActionIcon>
        )}
        <span className="rr-inbox2__avatar-wrap">
          <ContactAvatar name={name} size={40} />
          <ChannelMark channel={channel} size={16} />
        </span>
        <div className="rr-inbox2__header-name">
          <button type="button" className="rr-inbox2__header-title" onClick={onOpenContact} title={name ?? undefined}>
            {name}
          </button>
          {/*
            The line under the name answers "how do I reach this person?".
            On WhatsApp that is their number. On Messenger and Instagram there
            is no number and never will be, so it names the channel instead,
            which is the honest answer to the same question.
          */}
          <span className="rr-inbox2__header-msisdn">
            {channel === 'whatsapp' ? (
              <>
                <WhatsAppGlyph size={13} />
                {conversation.contact_msisdn}
              </>
            ) : channel === 'voice' ? (
              <>
                <ChannelMark channel="voice" size={13} className="rr-channel-mark--inline" />
                {conversation.contact_msisdn}
              </>
            ) : channel === 'email' ? (
              /* The address is how this person is reached (it is already the heading when there is no name);
                 the subject is what the thread is about. */
              <>
                <ChannelMark channel="email" size={13} className="rr-channel-mark--inline" />
                {conversation.contact_display_name && (
                  <span className="rr-inbox2__header-address">{conversation.contact_ref}{subject ? ' · ' : ''}</span>
                )}
                {subject ? (
                  <span className="rr-inbox2__header-subject" data-testid="thread-subject" title={subject}>
                    {t('inbox.email.subjectLine', { subject })}
                  </span>
                ) : !conversation.contact_display_name && t('inbox.channel.email')}
              </>
            ) : (
              <>
                <ChannelMark channel={channel} size={13} className="rr-channel-mark--inline" />
                {t(`inbox.channel.${channel === 'instagram' ? 'instagram' : channel === 'web' ? 'web' : 'messenger'}`)}
              </>
            )}
          </span>
        </div>
      </div>

      {/* Two different clocks: the reply window is Meta's 24 hours, the SLA is
          the organisation's own promise. Only the SLA is conditional, because
          plenty of organisations run no policy at all. */}
      <div className="rr-inbox2__chips">
        {/* A reply window means nothing on a record of phone calls. */}
        {/* Nor on the website chat, which has none (a reply waits for the visitor). */}
        {/* Nor on email (4 October 2026), which is a letter, not a chat with a 24-hour rule. */}
        {channel !== 'voice' && channel !== 'web' && channel !== 'email' && <SessionWindowBadge window={sessionWindow} />}
        {sla && <SlaBadge sla={sla} now={now} />}
        {linkedTicket && <LinkedTicketChip ticket={linkedTicket} />}
        {blocked && (
          <Badge size="sm" color="red" variant="light" className="rr-inbox2__chip" leftSection={<BlockIcon />}>
            {t('inbox.block.blocked')}
          </Badge>
        )}
        {/* A third clock, of a kind: whether the bot is answering. A pause
            nobody could see is how the owner's bot went quiet for a week. */}
        {takeover?.name ? (
          <Badge size="sm" color="teal" variant="light" className="rr-inbox2__chip" data-testid="takeover-chip">
            {t('inbox.takeover.with', { name: takeover.name })}
          </Badge>
        ) : botPaused && (
          <Tooltip
            label={botResumesAt ? t('inbox.bot.resumesAt', { time: formatDateTime(botResumesAt) }) : t('inbox.bot.untilResolved')}
            position="bottom"
          >
            <Badge size="sm" color="orange" variant="light" className="rr-inbox2__chip" leftSection={<PauseIcon />}>
              {t('inbox.bot.paused')}
            </Badge>
          </Tooltip>
        )}
        {closed && (
          <span className={`rr-inbox2__state rr-inbox2__state--${stateCode}`}>
            {t(`inbox.state.${stateCode}`)}
          </span>
        )}
      </div>

      {/* The actions an agent uses on almost every chat stay as buttons: take over, resolve, the settings. The rest wait
          under More actions (owner, 15 October 2026), so the name and number are never squeezed out of the header. */}
      <div className="rr-inbox2__header-actions" data-guide="inbox-actions">
        {/* Looking at a chat never takes it over; this does, and so does sending the first reply. */}
        {takeover && !closed && (takeover.name
          ? <HeaderAction compact={compact} label={takeover.from ? t('inbox.takeover.handBackTo', { bot: takeover.from }) : t('inbox.takeover.handBack')}
              icon={<PauseIcon />} loading={takeover.pending} onClick={takeover.onHandBack} />
          : <HeaderAction compact={compact} label={t('inbox.takeover.takeOver')} icon={<PauseIcon />} loading={takeover.pending} onClick={takeover.onTakeOver} />)}
        <HeaderAction
          compact={compact}
          label={closed ? t('inbox.reopen') : t('inbox.resolve')}
          icon={closed ? <ReopenIcon /> : <CheckIcon />}
          loading={resolving}
          onClick={onResolve}
        />
        <Menu position="bottom-end">
          <Menu.Target>
            {compact ? (
              <ActionIcon variant="default" size="sm" aria-label={t('inbox.moreActions')} loading={locking} data-testid="inbox-more-actions">
                <MoreIcon />
              </ActionIcon>
            ) : (
              <Button size="xs" variant="default" leftSection={<MoreIcon />} loading={locking} data-testid="inbox-more-actions">
                {t('inbox.moreActions')}
              </Button>
            )}
          </Menu.Target>
          <Menu.Dropdown>
            {onBook && <Menu.Item leftSection={<CalendarIcon />} onClick={onBook}>{t('inbox.book')}</Menu.Item>}
            {onCreateTicket && <Menu.Item leftSection={<TicketIcon />} onClick={onCreateTicket}>{t('inbox.ticket.create')}</Menu.Item>}
            <Menu.Item leftSection={<TransferIcon />} onClick={onTransfer}>{t('inbox.transfer')}</Menu.Item>
            {/* The padlock: stop others replying while this person writes. Gone once it is theirs. */}
            {!lockHidden && (
              <Menu.Item leftSection={<LockIcon />} disabled={lockDisabled || locking} onClick={onLock}>
                {t('inbox.lock.menu')}
              </Menu.Item>
            )}
          </Menu.Dropdown>
        </Menu>
        <HeaderAction
          compact={compact}
          label={t('inbox.sidebar.toggle')}
          guide="inbox-settings"
          icon={<PanelIcon />}
          className={`rr-inbox2__side-toggle-btn${sidebarOpen ? ' is-active' : ''}`}
          pressed={sidebarOpen}
          controls={sidebarId}
          onClick={onToggleSidebar}
        />
      </div>
    </header>
  );
}

/**
 * One header action: a labelled button when there is room, an icon with the
 * same name (and a tooltip saying it) when there is not. The accessible name
 * is the same either way.
 */
function HeaderAction({ compact, label, icon, onClick, loading, className, pressed, controls, guide }: {
  compact: boolean;
  /** A mark for the in-console guide's tour. */
  guide?: string;
  label: string;
  icon: ReactNode;
  onClick: () => void;
  loading?: boolean;
  className?: string;
  pressed?: boolean;
  controls?: string;
}) {
  if (compact) {
    return (
      <Tooltip label={label} position="bottom">
        <ActionIcon
          variant="default"
          size="sm"
          className={className}
          aria-label={label}
          aria-pressed={pressed}
          aria-controls={controls}
          loading={loading}
          onClick={onClick}
          data-guide={guide}
        >
          {icon}
        </ActionIcon>
      </Tooltip>
    );
  }
  return (
    <Button
      size="xs"
      variant="default"
      className={className}
      leftSection={icon}
      aria-pressed={pressed}
      aria-controls={controls}
      loading={loading}
      onClick={onClick}
      data-guide={guide}
    >
      {label}
    </Button>
  );
}

function BackIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="10,3 5,8 10,13" />
    </svg>
  );
}
function CalendarIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="2.2" y="3.2" width="11.6" height="10.6" rx="1.6" />
      <path d="M2.2 6.6h11.6M5.4 1.8v2.6M10.6 1.8v2.6" />
    </svg>
  );
}
function TicketIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 4.5h12v2.2a1.5 1.5 0 0 0 0 2.6v2.2H2V9.3a1.5 1.5 0 0 0 0-2.6Z" />
      <path d="M10 4.5v7" strokeDasharray="1.2 1.4" />
    </svg>
  );
}
function TransferIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M2 5.5h9M8.5 3 11 5.5 8.5 8M14 10.5H5M7.5 8 5 10.5 7.5 13" />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="3,8.5 6.5,12 13,4.5" />
    </svg>
  );
}
function ReopenIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 8a5 5 0 1 0 1.5-3.6" />
      <polyline points="3,2 3,5 6,5" />
    </svg>
  );
}
function LockIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="3.5" y="7" width="9" height="6.5" rx="1.6" />
      <path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" />
    </svg>
  );
}
function BlockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.7" strokeLinecap="round" aria-hidden>
      <circle cx="8" cy="8" r="5.8" />
      <path d="M3.9 3.9l8.2 8.2" />
    </svg>
  );
}
function PauseIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.8" strokeLinecap="round" aria-hidden>
      <path d="M6 3.5v9M10 3.5v9" />
    </svg>
  );
}
function MoreIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden>
      <circle cx="3.5" cy="8" r="1.4" /><circle cx="8" cy="8" r="1.4" /><circle cx="12.5" cy="8" r="1.4" />
    </svg>
  );
}
function PanelIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <rect x="1.8" y="2.5" width="12.4" height="11" rx="1.8" />
      <path d="M10 2.5v11" />
      <path d="M4.5 6h3M4.5 8.5h3" />
    </svg>
  );
}
